import React, { useState, useEffect, useRef, useCallback } from 'react'
import { XMarkIcon, PlusIcon, MinusIcon } from '@heroicons/react/24/outline'

const MIN_ZOOM = 0.25
const MAX_ZOOM = 4
const ZOOM_STEP = 0.25

function clampZoom(z) {
    return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z))
}

function clampPan(nx, ny, zoom, viewportEl, baseW, baseH) {
    if (zoom <= 1 || !viewportEl || baseW <= 0 || baseH <= 0) {
        return { x: 0, y: 0 }
    }
    const vw = viewportEl.clientWidth
    const vh = viewportEl.clientHeight
    const scaledW = baseW * zoom
    const scaledH = baseH * zoom
    const maxX = Math.max(0, (scaledW - vw) / 2)
    const maxY = Math.max(0, (scaledH - vh) / 2)
    return {
        x: Math.max(-maxX, Math.min(maxX, nx)),
        y: Math.max(-maxY, Math.min(maxY, ny)),
    }
}

/**
 * Modal com zoom (+/−), área fixa sem scroll-wheel no zoom, e arraste para mover quando ampliado.
 */
const ZoomableImageModal = ({ isOpen, onClose, src, alt = 'Imagem' }) => {
    const [zoom, setZoom] = useState(1)
    const [pan, setPan] = useState({ x: 0, y: 0 })
    const [isDragging, setIsDragging] = useState(false)
    /** Tamanho de layout da imagem em 100% zoom (antes do scale no wrapper) */
    const [baseSize, setBaseSize] = useState({ w: 0, h: 0 })

    const viewportRef = useRef(null)
    const dragRef = useRef(null)

    useEffect(() => {
        if (isOpen) {
            setZoom(1)
            setPan({ x: 0, y: 0 })
            setBaseSize({ w: 0, h: 0 })
        }
    }, [isOpen, src])

    useEffect(() => {
        if (!isOpen) return undefined
        const onKey = (e) => {
            if (e.key === 'Escape') onClose()
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [isOpen, onClose])

    useEffect(() => {
        if (!isDragging) return undefined

        const onMove = (e) => {
            const d = dragRef.current
            const v = viewportRef.current
            if (!d || !v) return
            const nx = d.panX + (e.clientX - d.startX)
            const ny = d.panY + (e.clientY - d.startY)
            setPan(clampPan(nx, ny, zoom, v, baseSize.w, baseSize.h))
        }

        const onUp = () => {
            setIsDragging(false)
            dragRef.current = null
        }

        window.addEventListener('mousemove', onMove)
        window.addEventListener('mouseup', onUp)
        return () => {
            window.removeEventListener('mousemove', onMove)
            window.removeEventListener('mouseup', onUp)
        }
    }, [isDragging, zoom, baseSize.w, baseSize.h])

    const onViewportMouseDown = useCallback(
        (e) => {
            if (zoom <= 1) return
            if (e.button !== 0) return
            e.preventDefault()
            setIsDragging(true)
            dragRef.current = {
                startX: e.clientX,
                startY: e.clientY,
                panX: pan.x,
                panY: pan.y,
            }
        },
        [zoom, pan.x, pan.y]
    )

    const handleImgLoad = useCallback((e) => {
        const { clientWidth, clientHeight } = e.target
        requestAnimationFrame(() => {
            setBaseSize({ w: clientWidth, h: clientHeight })
        })
    }, [])

    const applyZoom = useCallback((delta) => {
        setZoom((z) => {
            const nz = clampZoom(z + delta)
            return nz
        })
    }, [])

    /** Zoom ≤ 100% sem deslocamento; acima disso mantém pan dentro dos limites da imagem ampliada */
    useEffect(() => {
        if (zoom <= 1) {
            setPan({ x: 0, y: 0 })
            return
        }
        if (!viewportRef.current || baseSize.w <= 0) return
        setPan((p) => clampPan(p.x, p.y, zoom, viewportRef.current, baseSize.w, baseSize.h))
    }, [zoom, baseSize.w, baseSize.h])

    if (!isOpen || !src) return null

    const showGrab = zoom > 1

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-3 sm:p-4"
            onClick={onClose}
            role="presentation"
        >
            <div
                className="relative flex w-full max-w-[min(92vw,1280px)] flex-col overflow-hidden rounded-xl bg-gray-950/95 shadow-2xl ring-1 ring-white/10"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/10 px-3 py-2">
                    <span className="truncate pr-2 text-sm text-gray-200" title={alt}>
                        {alt}
                    </span>
                    <div className="flex flex-shrink-0 items-center gap-1">
                        <button
                            type="button"
                            onClick={() => applyZoom(-ZOOM_STEP)}
                            disabled={zoom <= MIN_ZOOM}
                            className="rounded-lg p-2 text-gray-200 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40"
                            aria-label="Reduzir zoom"
                        >
                            <MinusIcon className="h-5 w-5" />
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setZoom(1)
                                setPan({ x: 0, y: 0 })
                            }}
                            className="min-w-[3.75rem] rounded-lg px-2 py-1.5 text-xs font-medium text-gray-200 transition hover:bg-white/10"
                            title="Restaurar zoom e posição (100%)"
                        >
                            {Math.round(zoom * 100)}%
                        </button>
                        <button
                            type="button"
                            onClick={() => applyZoom(ZOOM_STEP)}
                            disabled={zoom >= MAX_ZOOM}
                            className="rounded-lg p-2 text-gray-200 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40"
                            aria-label="Ampliar zoom"
                        >
                            <PlusIcon className="h-5 w-5" />
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="ml-1 rounded-lg p-2 text-gray-200 transition hover:bg-white/10"
                            aria-label="Fechar"
                        >
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                    </div>
                </div>
                <div
                    ref={viewportRef}
                    className={`relative h-[min(80vh,900px)] w-full overflow-hidden bg-black/50 select-none ${
                        showGrab ? (isDragging ? 'cursor-grabbing' : 'cursor-grab') : 'cursor-default'
                    }`}
                    onMouseDown={onViewportMouseDown}
                    role="presentation"
                >
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                        <div
                            style={{
                                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                                transformOrigin: 'center center',
                            }}
                        >
                            <img
                                src={src}
                                alt={alt}
                                className="max-h-[min(75vh,820px)] max-w-[min(86vw,1160px)] h-auto w-auto rounded-lg object-contain shadow-lg"
                                draggable={false}
                                onLoad={handleImgLoad}
                            />
                        </div>
                    </div>
                </div>
                <p className="border-t border-white/10 px-3 py-2 text-center text-xs text-gray-400">
                    Zoom apenas com + e −. Com mais de 100%, arraste na área escura para mover a imagem. Escape para fechar.
                </p>
            </div>
        </div>
    )
}

export default ZoomableImageModal
